const GAME_STATE_KEY = 'scrabble.singlePlayer.gameState.v1';
const PLAYER_STATE_KEY = 'scrabble.singlePlayer.playerState.v1';
const PLAYER_STATS_KEY = 'scrabble.singlePlayer.playerStats.v1';
const GAME_STATE_EVENT = 'scrabble:game-state-changed';
const PLAYER_STATS_EVENT = 'scrabble:player-stats-changed';
const STORAGE_QUOTA_EXCEEDED_EVENT = 'scrabble:storage-quota-exceeded';
const GAME_STATE_WRITE_DEBOUNCE_MS = 90;

const LAST_WRITTEN_BY_KEY = {
  [GAME_STATE_KEY]: null,
  [PLAYER_STATE_KEY]: null,
  [PLAYER_STATS_KEY]: null,
};

let pendingGameStateWrite = null;
let pendingGameStateTimeoutId = null;
let gameStateFlushBound = false;

const canUseStorage = () => typeof window !== 'undefined' && Boolean(window.localStorage);

const emitGameStateChanged = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new Event(GAME_STATE_EVENT));
};

const emitPlayerStatsChanged = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new Event(PLAYER_STATS_EVENT));
};

const safeParse = (value) => {
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const safeStringify = (value) => {
  try {
    return JSON.stringify(value);
  } catch (error) {
    return null;
  }
};

const writeStorageValue = (key, value, emitEvent) => {
  if (!canUseStorage()) {
    return;
  }

  const serialized = safeStringify(value);
  if (!serialized) {
    return;
  }

  const lastWritten = LAST_WRITTEN_BY_KEY[key];
  if (serialized === lastWritten) {
    return;
  }

  const existing = window.localStorage.getItem(key);
  if (serialized === existing) {
    LAST_WRITTEN_BY_KEY[key] = serialized;
    return;
  }

  try {
    window.localStorage.setItem(key, serialized);
    LAST_WRITTEN_BY_KEY[key] = serialized;

    if (typeof emitEvent === 'function') {
      emitEvent();
    }
  } catch (err) {
    if (err && err.name === 'QuotaExceededError') {
      window.dispatchEvent(
        new CustomEvent(STORAGE_QUOTA_EXCEEDED_EVENT, { detail: { key } })
      );
    }
  }
};

const flushPendingGameStateWrite = () => {
  if (!pendingGameStateWrite) {
    return;
  }

  writeStorageValue(GAME_STATE_KEY, pendingGameStateWrite, emitGameStateChanged);
  pendingGameStateWrite = null;
};

const bindGameStateFlushHandlers = () => {
  if (gameStateFlushBound || typeof window === 'undefined') {
    return;
  }

  gameStateFlushBound = true;

  // Flush queued state if the tab is backgrounded or unloading.
  window.addEventListener('beforeunload', flushPendingGameStateWrite);
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushPendingGameStateWrite();
    }
  });
};

const subscribeToStorageKey = (key, listener) => {
  if (typeof window === 'undefined' || typeof listener !== 'function') {
    return () => {};
  }

  const handleStorage = (event) => {
    if (!event || event.key === key || event.key === null) {
      listener();
    }
  };

  window.addEventListener('storage', handleStorage);

  return () => {
    window.removeEventListener('storage', handleStorage);
  };
};

const deepMerge = (base, override) => {
  if (Array.isArray(base)) {
    return Array.isArray(override) ? override : base;
  }

  if (!isObject(base)) {
    return override === undefined ? base : override;
  }

  const result = { ...base };
  const source = isObject(override) ? override : {};

  Object.keys(source).forEach((key) => {
    result[key] = deepMerge(base[key], source[key]);
  });

  return result;
};

const createDefaultPlayerStats = () => ({
  meta: {
    schemaVersion: 1,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    lastPlayedAt: null,
  },
  runs: {
    started: 0,
    resumed: 0,
    completed: 0,
    won: 0,
    failed: 0,
    abandoned: 0,
    highestRoundReached: 0,
    highestScoreReached: 0,
    presetUsage: {
      classic: 0,
      random: 0,
      test: 0,
    },
  },
  scoring: {
    lifetimePoints: 0,
    lifetimeScoreFromWords: 0,
    lifetimeSubmittedWords: 0,
    lifetimeSubmitsUsed: 0,
    lifetimeDiscardsUsed: 0,
    lifetimeDiscardedTiles: 0,
    bestWord: {
      word: '',
      score: 0,
    },
    bestSubmit: {
      score: 0,
      wordCount: 0,
    },
    averageWordScore: 0,
    averageSubmitScore: 0,
  },
  economy: {
    totalShopVisits: 0,
    totalShopSelections: 0,
    totalShopSkips: 0,
    totalRoundsCompleted: 0,
    totalTargetsReached: 0,
    shopSelectionsByKind: {},
    shopSelectionsByRarity: {},
  },
  words: {
    frequency: {},
    longestWord: {
      word: '',
      length: 0,
    },
  },
  history: {
    recentRuns: [],
    recentSubmits: [],
    topWordPlays: [],
    topLongestWordPlays: [],
  },
});

const normalizePlayerStats = (stats) => {
  const defaults = createDefaultPlayerStats();
  const merged = deepMerge(defaults, stats || {});

  merged.meta.createdAt = Number(merged.meta.createdAt) || defaults.meta.createdAt;
  merged.meta.updatedAt = Number(merged.meta.updatedAt) || Date.now();
  merged.meta.lastPlayedAt = merged.meta.lastPlayedAt ? Number(merged.meta.lastPlayedAt) : null;

  if (!Array.isArray(merged.history.recentRuns)) {
    merged.history.recentRuns = [];
  }

  if (!Array.isArray(merged.history.recentSubmits)) {
    merged.history.recentSubmits = [];
  }

  if (!Array.isArray(merged.history.topWordPlays)) {
    merged.history.topWordPlays = [];
  }

  if (!Array.isArray(merged.history.topLongestWordPlays)) {
    merged.history.topLongestWordPlays = [];
  }

  if (!isObject(merged.words.frequency)) {
    merged.words.frequency = {};
  }

  return merged;
};

export const getStoredGameState = () => {
  if (!canUseStorage()) {
    return null;
  }

  const raw = window.localStorage.getItem(GAME_STATE_KEY);
  return raw ? safeParse(raw) : null;
};

export const writeStoredGameState = (state) => {
  if (!canUseStorage()) {
    return;
  }

  bindGameStateFlushHandlers();
  pendingGameStateWrite = state;

  if (pendingGameStateTimeoutId) {
    window.clearTimeout(pendingGameStateTimeoutId);
  }

  pendingGameStateTimeoutId = window.setTimeout(() => {
    pendingGameStateTimeoutId = null;
    flushPendingGameStateWrite();
  }, GAME_STATE_WRITE_DEBOUNCE_MS);
};

export const flushStoredGameState = () => {
  if (!canUseStorage()) {
    return;
  }

  if (pendingGameStateTimeoutId) {
    window.clearTimeout(pendingGameStateTimeoutId);
    pendingGameStateTimeoutId = null;
  }

  flushPendingGameStateWrite();
};

export const clearStoredGameState = () => {
  if (!canUseStorage()) {
    return;
  }

  if (pendingGameStateTimeoutId) {
    window.clearTimeout(pendingGameStateTimeoutId);
    pendingGameStateTimeoutId = null;
  }
  pendingGameStateWrite = null;

  window.localStorage.removeItem(GAME_STATE_KEY);
  LAST_WRITTEN_BY_KEY[GAME_STATE_KEY] = null;
  emitGameStateChanged();
};

export const GAME_STATE_CHANGED_EVENT = GAME_STATE_EVENT;
export const PLAYER_STATS_CHANGED_EVENT = PLAYER_STATS_EVENT;
export const STORAGE_QUOTA_EXCEEDED_EVENT_NAME = STORAGE_QUOTA_EXCEEDED_EVENT;

export const getStoredPlayerState = () => {
  if (!canUseStorage()) {
    return null;
  }

  const raw = window.localStorage.getItem(PLAYER_STATE_KEY);
  return raw ? safeParse(raw) : null;
};

export const writeStoredPlayerState = (state) => {
  if (!canUseStorage()) {
    return;
  }

  writeStorageValue(PLAYER_STATE_KEY, state);
};

export const getStoredPlayerStats = () => {
  if (!canUseStorage()) {
    return createDefaultPlayerStats();
  }

  const raw = window.localStorage.getItem(PLAYER_STATS_KEY);
  const parsed = raw ? safeParse(raw) : null;
  return normalizePlayerStats(parsed);
};

export const writeStoredPlayerStats = (stats) => {
  if (!canUseStorage()) {
    return;
  }

  const normalized = normalizePlayerStats(stats);
  normalized.meta.updatedAt = Date.now();
  if (!normalized.meta.createdAt) {
    normalized.meta.createdAt = Date.now();
  }

  writeStorageValue(PLAYER_STATS_KEY, normalized, emitPlayerStatsChanged);
};

export const updateStoredPlayerStats = (updaterOrPatch) => {
  const current = getStoredPlayerStats();

  const nextCandidate =
    typeof updaterOrPatch === 'function'
      ? updaterOrPatch(current)
      : deepMerge(current, updaterOrPatch || {});

  const next = normalizePlayerStats(nextCandidate || current);
  next.meta.updatedAt = Date.now();
  if (!next.meta.createdAt) {
    next.meta.createdAt = Date.now();
  }

  writeStoredPlayerStats(next);
  return next;
};

export const subscribeToGameStateChanges = (listener) => {
  if (typeof window === 'undefined' || typeof listener !== 'function') {
    return () => {};
  }

  window.addEventListener(GAME_STATE_EVENT, listener);
  const unsubscribeStorage = subscribeToStorageKey(GAME_STATE_KEY, listener);

  return () => {
    window.removeEventListener(GAME_STATE_EVENT, listener);
    unsubscribeStorage();
  };
};

export const resetStoredPlayerStats = () => {
  if (!canUseStorage()) {
    return;
  }

  clearStoredGameState();
  window.localStorage.removeItem(PLAYER_STATS_KEY);
  LAST_WRITTEN_BY_KEY[PLAYER_STATS_KEY] = null;

  const fresh = createDefaultPlayerStats();
  writeStoredPlayerStats(fresh);
};

export const subscribeToPlayerStatsChanges = (listener) => {
  if (typeof window === 'undefined' || typeof listener !== 'function') {
    return () => {};
  }

  window.addEventListener(PLAYER_STATS_EVENT, listener);
  const unsubscribeStorage = subscribeToStorageKey(PLAYER_STATS_KEY, listener);

  return () => {
    window.removeEventListener(PLAYER_STATS_EVENT, listener);
    unsubscribeStorage();
  };
};
